import { useEffect, useMemo, useState } from "react";
import { Hourglass, Lightning, Pulse } from "@phosphor-icons/react";

/**
 * VitaMeaTicker — compteur en temps réel des jours de vie sacrifiés
 * depuis le 1er janvier, basé sur V1 (jours/an) de la dernière analyse.
 *
 * - Tick à 100 ms (lissage sub-seconde)
 * - Fraction de jour affichée à 4 décimales (≈ précision seconde-pulse)
 * - Affiche aussi la dette personnelle D2 qui s'accumule au même rythme
 */
export default function VitaMeaTicker({ v1Annuel = 0, d2Dette = 0, devise = "UM" }) {
  // Year start in UTC (immutable across ticks)
  const yearStart = useMemo(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), 0, 1, 0, 0, 0));
  }, []);

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 100);
    return () => clearInterval(id);
  }, []);

  if (!v1Annuel || v1Annuel <= 0) return null;

  const secondsElapsed = Math.max(0, (now - yearStart) / 1000);
  const secondsPerYear = 365.25 * 24 * 3600;
  const ratePerSecond = v1Annuel / secondsPerYear;
  const cumul = secondsElapsed * ratePerSecond;

  const dettePerSecond = d2Dette > 0 ? d2Dette / secondsPerYear : 0;
  const detteCumul = secondsElapsed * dettePerSecond;

  // Percent of year + current rate per minute / per hour for context
  const percentOfYear = (secondsElapsed / secondsPerYear) * 100;
  const ratePerMinute = ratePerSecond * 60;
  const ratePerHour = ratePerSecond * 3600;

  const fmtDay = (n) => n.toFixed(7);
  const fmtPct = (n) => n.toFixed(5) + "%";

  return (
    <div
      className="border-2 border-[var(--signal)] bg-[var(--ink)] text-[var(--paper)] p-6 lg:p-8 relative overflow-hidden"
      data-testid="vita-mea-ticker"
    >
      {/* Pulse overlay */}
      <div className="absolute top-3 right-3 flex items-center gap-2 label-mono text-[var(--signal)]">
        <Pulse size={14} weight="fill" className="animate-pulse" />
        <span>VITA-MEA · LIVE</span>
      </div>

      <div className="flex items-start gap-4">
        <Hourglass size={28} weight="fill" className="text-[var(--signal)] mt-1 hidden sm:block" />
        <div>
          <div className="label-mono text-[var(--paper)]/60">§ Compteur vitaliste · {now.getUTCFullYear()}</div>
          <h2 className="font-serif text-xl sm:text-2xl mt-1 leading-tight">
            Depuis le 1<sup>er</sup> janvier, vous avez cédé
          </h2>
        </div>
      </div>

      {/* Counter */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
        <div className="bg-[var(--ink)] p-5">
          <div className="label-mono text-[var(--paper)]/60">V1 cumulé</div>
          <div
            className="font-serif text-3xl sm:text-4xl mt-2 tabular-nums tracking-tight"
            data-testid="vita-mea-cumul"
          >
            {fmtDay(cumul)}
            <span className="text-base text-[var(--paper)]/60 ml-2 font-mono">jours</span>
          </div>
          <div className="label-mono text-[var(--paper)]/40 mt-2">
            ↑ {fmtPct(percentOfYear)} de l'année · {ratePerMinute.toFixed(5)} j/min · {ratePerHour.toFixed(4)} j/h
          </div>
        </div>
        <div className="bg-[var(--ink)] p-5">
          <div className="label-mono text-[var(--paper)]/60">D2 cumulé · dette</div>
          <div className="font-serif text-3xl sm:text-4xl mt-2 tabular-nums tracking-tight" data-testid="vita-mea-dette">
            {d2Dette > 0 ? fmtDay(detteCumul) : "—"}
            <span className="text-base text-[var(--paper)]/60 ml-2 font-mono">{d2Dette > 0 ? "jours" : ""}</span>
          </div>
          <div className="label-mono text-[var(--paper)]/40 mt-2">
            {d2Dette > 0
              ? `↑ rythme : ${dettePerSecond.toExponential(2)} j/sec`
              : "Aucune dette publique mesurée dans le dernier rapport."}
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2 text-xs font-serif italic text-[var(--paper)]/70 leading-relaxed">
        <Lightning size={14} className="text-[var(--signal)] shrink-0" />
        <span>
          Chaque battement de cette seconde, l'État absorbe en moyenne {(ratePerSecond * 86400).toFixed(4)} jour(s) de votre vie ·
          {" "}devise {devise}.
        </span>
      </div>
    </div>
  );
}
